import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { ConnectionCredentialsPanel } from '@/components/platform/ConnectionCredentialsPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { CapabilityChips, CredentialReference } from '@/components/platform/CapabilityChips';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  isVaultReference,
  type IntegrationProbeResult,
  type PlatformAuditRecord,
  type PlatformConnection,
  type PlatformIncident,
  type UpdatePlatformConnectionRequest,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * One shared connection: what it is, whether it works, and who may change it.
 *
 * THE PROBE IS SERVER-SIDE AND CANNOT BE ANYWHERE ELSE. The credential is a vault
 * pointer that never leaves that process, so there is no version of this test a
 * browser could perform. A failure of the test button is therefore reported as a
 * failure of the PROBE, never as a verdict on the connection — "we could not
 * check" and "it is broken" send an operator to different places.
 *
 * ROTATION CHANGES A POINTER, NOT A SECRET. The rotation dialog accepts a
 * `vault://` reference and refuses anything else in the browser. There is no
 * variant of it that takes a value, deliberately: the server rejects one, and a
 * field that would accept it is itself the hazard.
 */

export function PlatformConnectionDetailPage() {
  const { connectionId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.connections.get(connectionId), [connectionId]);
  const { state, reload, replace } = useAsyncData<PlatformConnection>(
    `connection:${connectionId}`,
    fetcher,
    connectionId.length > 0,
  );

  const [probing, setProbing] = useState(false);
  const [probeError, setProbeError] = useState<ApiError | null>(null);
  const [probeOutcome, setProbeOutcome] = useState<IntegrationProbeResult | null>(null);
  const [statusRecorded, setStatusRecorded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);
  const [disableSubmitting, setDisableSubmitting] = useState(false);

  const testConnection = async () => {
    setProbing(true);
    setProbeError(null);
    setProbeOutcome(null);
    try {
      const result = await platformApi.connections.testConnection(connectionId);
      replace(result.connection);
      setProbeOutcome(result.outcome);
      setStatusRecorded(result.statusRecorded);
    } catch (cause) {
      setProbeError(
        cause instanceof ApiError ? cause : new ApiError((cause as Error).message, 0, connectionId),
      );
    } finally {
      setProbing(false);
    }
  };

  const disable = async (reason: string) => {
    setDisableSubmitting(true);
    setDisableError(null);
    try {
      replace(await platformApi.connections.disable(connectionId, reason));
      setDisabling(false);
    } catch (cause) {
      setDisableError((cause as Error).message);
    } finally {
      setDisableSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('platform.connections.breadcrumbLabel')}>
        <Link to="/admin/integrations">{t('platform.connections.title')}</Link>
        <Icon name="chevronRight" size={13} />
        <span>{state.status === 'ready' ? state.data.displayName : connectionId}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(connection) => (
          <>
            <header className={styles.caseHeader}>
              <div>
                <h1 className={styles.pageTitle}>{connection.displayName}</h1>
                <p className={styles.pageLead}>
                  {t(`platform.system.${connection.systemCode}`, { defaultValue: connection.systemCode })}
                  {` · ${t(`platform.environment.${connection.environment}`)}`}
                </p>
              </div>
              <div className={styles.caseHeaderAside}>
                <StateBadge kind="integration" value={connection.status} />
              </div>
            </header>

            <Panel
              title={t('platform.connections.healthTitle')}
              icon="pulse"
              actions={
                <button
                  type="button"
                  className={styles.primaryBtnSm}
                  onClick={testConnection}
                  disabled={probing || connection.status === 'DISABLED'}
                >
                  <Icon name="refresh" size={15} />
                  {probing ? t('platform.connections.testing') : t('platform.connections.test')}
                </button>
              }
            >
              <p className={styles.dataNotice}>
                <Icon name="info" size={15} aria-hidden="true" />
                {t('platform.connections.healthNotice')}
              </p>

              <dl className={styles.detailGrid}>
                <dt>{t('platform.connections.colStatus')}</dt>
                <dd>
                  <StateBadge kind="integration" value={connection.status} size="sm" />
                  <span className={styles.fieldHint}>
                    {t(`platform.state.integrationMeaning.${connection.status}`, { defaultValue: '' })}
                  </span>
                </dd>
                <dt>{t('platform.connections.lastCheck')}</dt>
                <dd>
                  {connection.lastCheckedAt
                    ? `${formatDateTime(connection.lastCheckedAt, locale)} (${formatRelative(connection.lastCheckedAt, locale)})`
                    : t('platform.connections.neverChecked')}
                </dd>
                <dt>{t('platform.connections.lastSuccessful')}</dt>
                <dd>
                  {connection.lastSuccessfulAt
                    ? formatDateTime(connection.lastSuccessfulAt, locale)
                    : t('platform.connections.neverSucceededLong')}
                </dd>
                <dt>{t('platform.connections.fieldBaseUrl')}</dt>
                <dd>{connection.baseUrl || '—'}</dd>
                <dt>{t('platform.connections.fieldHealthUrl')}</dt>
                <dd>{connection.healthCheckUrl || t('platform.connections.healthUrlDefaulted')}</dd>
                <dt>{t('platform.connections.fieldApiVersion')}</dt>
                <dd>{connection.apiVersion || '—'}</dd>
                <dt>{t('platform.connections.fieldTimeout')}</dt>
                <dd>
                  {connection.timeoutMs === undefined
                    ? '—'
                    : t('platform.connections.timeoutValue', { ms: connection.timeoutMs })}
                </dd>
                <dt>{t('platform.connections.fieldRetry')}</dt>
                <dd>
                  {connection.retryPolicy
                    ? t('platform.connections.retryValue', {
                        attempts: connection.retryPolicy.maxAttempts,
                        backoff: connection.retryPolicy.backoffMs,
                        statuses: connection.retryPolicy.retryOn.join(', ') || '—',
                      })
                    : t('platform.connections.retryNone')}
                </dd>
              </dl>

              {connection.lastError && (
                <p className={styles.rowDetailError} role="status">
                  {connection.lastError}
                </p>
              )}

              {probeError && <FailureNotice error={probeError} onRetry={testConnection} />}

              {probeOutcome && (
                <div className={styles.warningNotice} role="status">
                  <Icon name="pulse" size={16} aria-hidden="true" />
                  <div>
                    <p className={styles.warningTitle}>
                      {t(`platform.integrations.probe.${probeOutcome.kind}`, {
                        defaultValue: probeOutcome.kind,
                      })}
                    </p>
                    <p className={styles.warningBody}>{probeOutcome.detail}</p>
                    <p className={styles.rowMetaLine}>
                      {probeOutcome.responseTimeMs !== null
                        ? t('platform.integrations.probeLatency', { ms: probeOutcome.responseTimeMs })
                        : t('platform.integrations.probeNoRoundTrip')}
                      {probeOutcome.httpStatus !== null
                        ? ` · ${t('platform.integrations.probeStatus', { status: probeOutcome.httpStatus })}`
                        : ''}
                    </p>
                    {!statusRecorded && (
                      <p className={styles.rowDetail}>{t('platform.connections.probeNotRecorded')}</p>
                    )}
                  </div>
                </div>
              )}
            </Panel>

            <Panel
              title={t('platform.connections.configurationTitle')}
              icon="settings"
              actions={
                <button type="button" className={styles.secondaryBtn} onClick={() => setEditing(true)}>
                  <Icon name="settings" size={15} />
                  {t('platform.connections.editEndpoint')}
                </button>
              }
            >
              <dl className={styles.detailGrid}>
                <dt>{t('platform.connections.colAuthentication')}</dt>
                <dd>
                  {t(`platform.authentication.${connection.authenticationType}`, {
                    defaultValue: connection.authenticationType,
                  })}
                </dd>
                <dt>{t('platform.connections.fieldClientId')}</dt>
                <dd>{connection.clientId || '—'}</dd>
                <dt>{t('platform.connections.fieldVaultRef')}</dt>
                <dd>
                  <CredentialReference reference={connection.credentialVaultReference} />
                  <span className={styles.fieldHint}>{t('platform.connections.credentialPointerHint')}</span>
                </dd>
                <dt>{t('platform.connections.updatedBy')}</dt>
                <dd>
                  {connection.updatedBy || '—'}
                  {connection.updatedAt ? ` · ${formatDateTime(connection.updatedAt, locale)}` : ''}
                </dd>
              </dl>

              <div className={styles.transitionRow}>
                <button type="button" className={styles.secondaryBtn} onClick={() => setRotating(true)}>
                  <Icon name="switch" size={15} />
                  {t('platform.connections.rotate')}
                </button>
                {connection.status !== 'DISABLED' && (
                  <button type="button" className={styles.dangerBtn} onClick={() => setDisabling(true)}>
                    <Icon name="ban" size={15} />
                    {t('platform.connections.disable')}
                  </button>
                )}
              </div>
            </Panel>

            <ConnectionCredentialsPanel connectionId={connectionId} />

            <Panel title={t('platform.connections.capabilitiesTitle')} icon="integration">
              <p className={styles.fieldHint}>{t('platform.connections.capabilitiesLead')}</p>
              <CapabilityChips
                systemCode={connection.systemCode}
                capabilities={connection.capabilities}
              />
            </Panel>

            <ConnectionIncidentsPanel />
            <ConnectionAuditPanel connectionId={connectionId} locale={locale} />

            <EditConnectionDialog
              open={editing}
              connection={connection}
              onClose={() => setEditing(false)}
              onSaved={(updated) => {
                replace(updated);
                setEditing(false);
              }}
            />

            <RotateReferenceDialog
              open={rotating}
              connection={connection}
              onClose={() => setRotating(false)}
              onRotated={(updated) => {
                replace(updated);
                setRotating(false);
              }}
            />

            {disabling && (
              <ConfirmActionDialog
                open
                title={t('platform.connections.disableTitle', { name: connection.displayName })}
                intro={t('platform.connections.disableIntro', { name: connection.displayName })}
                tone="critical"
                impactStatus="ready"
                impact={
                  <ul className={styles.impactList}>
                    <li>{t('platform.connections.disableImpactAll')}</li>
                    <li>{t('platform.connections.disableImpactMappings')}</li>
                    <li>{t('platform.connections.disableImpactProbe')}</li>
                  </ul>
                }
                // Typed out in full because this row is shared by every
                // institution: a misclick here is a platform-wide outage, not a
                // one-tenant mistake.
                confirmPhrase={connection.systemCode}
                confirmLabel={t('platform.connections.disable')}
                submitting={disableSubmitting}
                error={disableError}
                onCancel={() => setDisabling(false)}
                onConfirm={disable}
              />
            )}
          </>
        )}
      </AsyncPanel>
    </div>
  );
}

/* ─── Edit endpoint ─── */

/**
 * Amends where the connection points and how patiently it is called.
 *
 * The system code and the environment are absent, and not because the form ran
 * out of room: they are the identity of the row and the pair its uniqueness
 * constraint is built on. Editing either in place would silently re-point every
 * institution mapping hanging off this id at a different system. That is a new
 * registration, and it should look like one.
 */
function EditConnectionDialog({
  open,
  connection,
  onClose,
  onSaved,
}: {
  open: boolean;
  connection: PlatformConnection;
  onClose: () => void;
  onSaved: (connection: PlatformConnection) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<UpdatePlatformConnectionRequest>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      displayName: connection.displayName,
      baseUrl: connection.baseUrl ?? '',
      healthCheckUrl: connection.healthCheckUrl ?? '',
      apiVersion: connection.apiVersion ?? '',
      timeoutMs: connection.timeoutMs,
    });
    setError(null);
  }, [open, connection]);

  const urlPattern = import.meta.env.DEV ? /^https?:\/\/\S+$/ : /^https:\/\/\S+$/;
  const urlValid = !form.baseUrl || urlPattern.test(form.baseUrl);
  const healthUrlValid = !form.healthCheckUrl || urlPattern.test(form.healthCheckUrl);
  const valid = (form.displayName ?? '').trim().length >= 2 && urlValid && healthUrlValid;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      onSaved(
        await platformApi.connections.update(connection.id, {
          displayName: form.displayName?.trim(),
          baseUrl: form.baseUrl?.trim() || undefined,
          healthCheckUrl: form.healthCheckUrl?.trim() || undefined,
          apiVersion: form.apiVersion?.trim() || undefined,
          timeoutMs: form.timeoutMs,
        }),
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.connections.editTitle')}
      description={t('platform.connections.editLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.connections.fieldName')}</span>
          <input
            className={styles.input}
            value={form.displayName ?? ''}
            required
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          />
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldBaseUrl')}</span>
            <input
              className={styles.input}
              type="url"
              value={form.baseUrl ?? ''}
              aria-invalid={(form.baseUrl ?? '').length > 0 && !urlValid}
              onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldHealthUrl')}</span>
            <input
              className={styles.input}
              type="url"
              value={form.healthCheckUrl ?? ''}
              aria-invalid={(form.healthCheckUrl ?? '').length > 0 && !healthUrlValid}
              onChange={(event) => setForm({ ...form, healthCheckUrl: event.target.value })}
            />
          </label>
        </div>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldApiVersion')}</span>
            <input
              className={styles.input}
              value={form.apiVersion ?? ''}
              onChange={(event) => setForm({ ...form, apiVersion: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldTimeout')}</span>
            <input
              className={styles.input}
              type="number"
              min={1000}
              max={120000}
              value={form.timeoutMs ?? ''}
              onChange={(event) =>
                setForm({
                  ...form,
                  timeoutMs: event.target.value === '' ? undefined : Number(event.target.value),
                })
              }
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldTimeoutHint')}</span>
          </label>
        </div>

        <p className={styles.fieldHint}>{t('platform.connections.editImmutableHint')}</p>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Rotate the credential reference ─── */

/**
 * Repoints the connection at a different vault path.
 *
 * Destructive enough to need a reason and a confirmation: the last health verdict
 * was earned by the OLD reference and stops applying the moment this changes, so a
 * rotation that goes to the wrong path leaves a connection that reads green and
 * cannot authenticate. The new value is validated as a pointer in the browser, so
 * a pasted credential is refused before it can leave the machine.
 */
function RotateReferenceDialog({
  open,
  connection,
  onClose,
  onRotated,
}: {
  open: boolean;
  connection: PlatformConnection;
  onClose: () => void;
  onRotated: (connection: PlatformConnection) => void;
}) {
  const { t } = useTranslation();
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReference('');
      setError(null);
    }
  }, [open]);

  const referenceValid = isVaultReference(reference.trim());

  const run = async (reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      onRotated(
        await platformApi.connections.rotateCredentialReference(connection.id, {
          credentialVaultReference: reference.trim(),
          reason,
        }),
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <ConfirmActionDialog
      open
      title={t('platform.connections.rotateTitle', { name: connection.displayName })}
      intro={t('platform.connections.rotateIntro')}
      tone="warning"
      impactStatus="ready"
      impact={
        <ul className={styles.impactListWarning}>
          <li>{t('platform.connections.rotateImpactVerdict')}</li>
          <li>{t('platform.connections.rotateImpactAll')}</li>
          <li>{t('platform.connections.rotateImpactValue')}</li>
        </ul>
      }
      extraValid={referenceValid}
      extraFields={
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.connections.rotateNewRef')}</span>
          <p className={styles.fieldHint}>{t('platform.connections.rotateCurrent')}</p>
          <CredentialReference reference={connection.credentialVaultReference} />
          <input
            className={styles.input}
            value={reference}
            placeholder="vault://campusone/connections/digital-switch#hmac_secret"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={reference.length > 0 && !referenceValid}
            aria-describedby="rotate-ref-hint"
            onChange={(event) => setReference(event.target.value)}
          />
          <span className={styles.fieldHint} id="rotate-ref-hint">
            {t('platform.connections.fieldVaultRefHint')}
          </span>
          {reference.length > 0 && !referenceValid && (
            <span className={styles.fieldError} role="alert">
              {t('platform.connections.vaultInvalid')}
            </span>
          )}
        </label>
      }
      confirmLabel={t('platform.connections.rotate')}
      submitting={submitting}
      error={error}
      onCancel={onClose}
      onConfirm={run}
    />
  );
}

/**
 * There is no "institutions on this connection" panel, deliberately.
 *
 * The mapping resource is mounted under the institution — the server offers no
 * route that lists mappings by connection — and a panel here would have to be
 * assembled by fetching every institution's mappings and filtering. Beyond being
 * slow, that list would be silently partial the moment one of those requests
 * failed, and a partial list of "who is affected" is the worst possible thing to
 * read while deciding whether to disable a shared connection. The disable dialog
 * says instead, in words, that this affects every institution.
 */

/* ─── Incidents ─── */

/**
 * Open platform incidents, unfiltered by connection.
 *
 * An incident in this service records affected institutions and products; it has
 * no field naming a connection. Filtering the list by a string match on the
 * connection's name would produce a panel that looks precise and silently omits
 * the incident somebody wrote up as "payments failing" — the exact one they need.
 * So the whole open list is shown and labelled as the whole open list.
 */
function ConnectionIncidentsPanel() {
  const { t } = useTranslation();
  const fetcher = useCallback(() => platformApi.operations.incidents({ openOnly: true }), []);
  const { state, reload } = useAsyncData<PlatformIncident[]>('connection-incidents', fetcher);

  return (
    <Panel title={t('platform.connections.incidentsTitle')} icon="warning">
      <p className={styles.fieldHint}>{t('platform.connections.incidentsLead')}</p>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.connections.incidentsEmpty')}
      >
        {(data) => (
          <ul className={styles.rowList}>
            {data.map((incident) => (
              <li key={incident.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>{incident.title}</p>
                  <p className={styles.rowMetaLine}>
                    {incident.reference}
                    {` · ${t(`platform.operations.incidentStatus.${incident.status}`, {
                      defaultValue: incident.status,
                    })}`}
                  </p>
                </div>
                <div className={styles.rowAside}>
                  <StateBadge kind="severity" value={severityBadgeValue(incident.severity)} size="sm" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </Panel>
  );
}

/**
 * Incident severities are CRITICAL/MAJOR/MINOR; the badge vocabulary is
 * INFO/WARNING/CRITICAL. Mapped rather than passed through, because an unmapped
 * value falls back to neutral grey and a MAJOR incident drawn grey is worse than
 * no badge at all.
 */
function severityBadgeValue(severity: PlatformIncident['severity']): string {
  switch (severity) {
    case 'CRITICAL':
      return 'CRITICAL';
    case 'MAJOR':
      return 'WARNING';
    case 'MINOR':
      return 'INFO';
  }
}

/* ─── Audit ─── */

function ConnectionAuditPanel({ connectionId, locale }: { connectionId: string; locale: string }) {
  const { t } = useTranslation();
  const fetcher = useCallback(() => platformApi.connections.audit(connectionId), [connectionId]);
  const { state, reload } = useAsyncData<PlatformAuditRecord[]>(
    `connection-audit:${connectionId}`,
    fetcher,
    connectionId.length > 0,
  );

  return (
    <Panel title={t('platform.connections.auditTitle')} icon="history">
      <p className={styles.fieldHint}>{t('platform.connections.auditLead')}</p>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.connections.auditEmpty')}
      >
        {(data) => (
          <ol className={styles.timeline}>
            {data.map((record) => (
              <li key={record.id} className={styles.timelineItem}>
                <span className={styles.timelineDot} aria-hidden="true" />
                <div>
                  <p className={styles.rowTitle}>{record.action}</p>
                  <p className={styles.rowMetaLine}>
                    {formatDateTime(record.occurredAt, locale)}
                    {record.actorName || record.actorId ? ` · ${record.actorName ?? record.actorId}` : ''}
                  </p>
                  {record.reason && <p className={styles.rowDetail}>{record.reason}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </AsyncPanel>
    </Panel>
  );
}
